import React, { useEffect, useState } from 'react';
import { FaFileAlt } from 'react-icons/fa';
import {
  Box,
  Grid,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Button,
  Typography,
} from '@tq/ui-components';
import { COLOURS } from '@tq/ui-components/dist/pp2/theme';
import { useTranslation } from 'react-i18next';
import { ExceptionLogDetails } from 'types';
import { exceptionLogsDetailsModalState, exceptionLogsDetailsState } from 'store';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useExceptionLogDetails } from 'api/trafficMonitor/hooks/useExceptionLogDetails';

export const ExceptionLogMonitorDetails: React.FC = () => {
  const { t } = useTranslation('traffic_monitor');
  const [isOpen, setIsOpen] = useRecoilState(exceptionLogsDetailsModalState);
  const detailsRequest = useRecoilValue(exceptionLogsDetailsState);
  const [exceptionDetails, setExceptionDetails] = useState<ExceptionLogDetails | undefined>();
  const modalHeight = '100vh - 7.5rem';
  const modalHeaderHeight = '4.0625rem';
  const urlSubtitleHeight = '3.625rem';
  const jsonMessageMarginX = '4rem';
  const modalFooterHeight = '6.8125rem';

  const { data: _exceptionDetails } = useExceptionLogDetails({
    exceptionLogType: detailsRequest?.exceptionLogType ?? 'ConsultationException',
    exceptionId: detailsRequest?.id ?? 0,
  });

  const formatJsonString = (jsonString: string) => {
    try {
      const dataObj = JSON.parse(jsonString);
      return JSON.stringify(dataObj, null, 4);
    } catch {
      return jsonString;
    }
  };

  useEffect(() => {
    if (detailsRequest && isOpen) {
      setExceptionDetails(_exceptionDetails as unknown as ExceptionLogDetails);
    }
  }, [_exceptionDetails, detailsRequest, isOpen]);

  const header = (
    <ModalHeader>
      <Grid gridAutoFlow='column' gridGap={4}>
        <FaFileAlt color={COLOURS.blue[600]} />
        <span>{t('exception_details', 'Exception details')}</span>
      </Grid>
      <ModalCloseButton />
    </ModalHeader>
  );

  function getModalBody() {
    return (
      <ModalBody p='0 !important'>
        <Box
          bg={COLOURS.gray[100]}
          borderTop={`1px solid ${COLOURS.gray[300]}`}
          borderBottom={`1px solid ${COLOURS.gray[300]}`}
          px={8}
          py={4}
        >
          <Grid templateColumns='repeat(2, 1fr)' gridGap={4}>
            <p>
              {t('exception_id', 'Id')}: {exceptionDetails?.id}
            </p>
            <p>
              {t('exception_log_type', 'Type')}: {exceptionDetails?.exceptionLogType}
            </p>
            <p>
              {t('application_reference_number', 'Application Reference Number')}:{' '}
              {exceptionDetails?.planningApplicationReferenceNo}
            </p>
            <p>
              {t('date', 'Date & time')}: {exceptionDetails?.date}
            </p>
            <p>
              {t('filename', 'File name')}: {exceptionDetails?.fileName}
            </p>
          </Grid>
        </Box>
        <Box
          h={`calc(${modalHeight} - ${modalHeaderHeight} - ${urlSubtitleHeight} - ${jsonMessageMarginX} - ${modalFooterHeight})`}
          overflow='auto'
        >
          <Box mb={8}>
            {
              <div>
                <Typography px={8} py={4} mt={8}>
                  <h3>{t('message', 'Message')}</h3>
                </Typography>
                <Box
                  mx={8}
                  p={4}
                  border={`1px solid ${COLOURS.gray[300]}`}
                  overflow='auto'
                  data-testid='message'
                >
                  <pre>{formatJsonString(exceptionDetails?.message ?? '')}</pre>
                </Box>
              </div>
            }
            <Typography px={8} py={4} mt={8}>
              <h3>{t('stackTrace', 'Stack Trace')}</h3>
            </Typography>
            <Box
              mx={8}
              p={4}
              border={`1px solid ${COLOURS.gray[300]}`}
              overflow='auto'
              data-testid='stackTrace'
            >
              <pre>{formatJsonString(exceptionDetails?.stackTrace ?? '')}</pre>
            </Box>
          </Box>
        </Box>
      </ModalBody>
    );
  }

  return (
    <>
      <Modal onClose={() => setIsOpen(false)} isOpen={isOpen} scrollBehavior='inside'>
        <ModalOverlay />
        <ModalContent fontSize='0.875rem'>
          {header}
          {getModalBody()}
          <ModalFooter>
            <Button onClick={() => setIsOpen(false)}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
